import * as React from 'react';
import { useTranslation } from 'react-i18next';
import BarChart from '@cloudscape-design/components/bar-chart';
import Box from '@cloudscape-design/components/box';
import LineChart from '@cloudscape-design/components/line-chart';
import { ChartCard } from './ChartCard';

export type BiTimePoint = { x: string; y: number };

type Props = {
  title: string;
  subtitle?: string;
  type: 'line' | 'bar';
  data: readonly BiTimePoint[];
  color?: string;
  valueFormatter: (value: number) => string;
};

/**
 * Gráfico de série temporal de uma série só (eixo X categórico de datas), em Cloudscape.
 *
 * Origem: sem issue — commit e5ac91703, 2026-06-09 00:31 +0200 (Sprint 39-G, Recharts →
 * Cloudscape; o `apps/admin` é todo Cloudscape, ver CLAUDE.md "UI web").
 *
 * Por quê: os gráficos do BI têm sempre uma série só por card, então filtro e legenda ficam
 * escondidos. O eixo X é categórico e recebe a data já formatada (`dd/mm/aaaa`, via
 * `formatYyyymmddDisplay`): o agregado é indexado por `yyyymmdd` em `America/Sao_Paulo`, e
 * nenhum `Date` é construído aqui — trocar para eixo temporal exigiria resolver esse fuso.
 *
 * Contrato: `type` escolhe `LineChart` ou `BarChart`; `valueFormatter` formata o eixo Y e o
 * popover — quem passa centavos divididos por 100 reconverte no formatter (ver `MrrChart`).
 * Sem efeito colateral. Altura fixa 230 px dentro do `ChartCard` de 16 rem.
 *
 * Usado por: `apps/admin` — charts de `dashboard-finance` (MRR, ARPT, NetNewMrr, Sales,
 * Users, Labels), `dashboard-sales` (NewSubscriptions, Cancellations, ChurnRate,
 * RevenueByCountry), `dashboard-usage`, `dashboard-usage-basic`, `dashboard-product`.
 *
 * Dicas: é componente puramente visual — unidade e moeda são decisão de quem chama.
 */
export function BiTimeChart({
  title,
  subtitle,
  type,
  data,
  color,
  valueFormatter,
}: Props): React.JSX.Element {
  const { t } = useTranslation();
  const points = data.map((p) => ({ x: p.x, y: p.y }));
  const empty = (
    <Box textAlign="center" color="inherit">
      {t('finance.empty')}
    </Box>
  );
  const common = {
    xScaleType: 'categorical' as const,
    height: 230,
    hideFilter: true,
    hideLegend: true,
    ariaLabel: title,
    i18nStrings: { yTickFormatter: valueFormatter },
    empty,
  };
  const colorProp = color !== undefined ? { color } : {};

  return (
    <ChartCard title={title} {...(subtitle !== undefined ? { subtitle } : {})}>
      {type === 'line' ? (
        <LineChart
          {...common}
          series={[{ title, type: 'line', data: points, valueFormatter, ...colorProp }]}
        />
      ) : (
        <BarChart
          {...common}
          series={[{ title, type: 'bar', data: points, valueFormatter, ...colorProp }]}
        />
      )}
    </ChartCard>
  );
}
